import { word } from "@/modules/core/utils/i18n"
import {
  type PayoutDetailsDto,
  type PayoutItemDto,
  type PayoutTransactionDto,
  type TransactionSummaryDto,
  TransferMethod,
} from "@modules/finances/financesPayout/gateway/payoutDto"
import { PayoutTransferStatus, TransactionStatus } from "@modules/finances/types/financesType"
import dayjs from "dayjs"

export type PayoutItemState = Omit<
  PayoutItemEntity,
  | "getFormatedDigits"
  | "getPayoutStatusWord"
  | "getPayoutStatusColor"
  | "getPayoutStatusDetailsWord"
  | "getPayoutStatusDetailsColor"
>
export class PayoutItemEntity {
  id: string
  amount: number
  currency: string
  status: PayoutTransferStatus
  last4Digits: string | null
  bankName: string | null
  createdAt: Date
  expectedReceptionDate: Date
  intervalStartDate: Date | null
  intervalEndDate: Date | null

  private constructor(payoutItemEntity: PayoutItemState) {
    this.id = payoutItemEntity.id
    this.amount = payoutItemEntity.amount
    this.currency = payoutItemEntity.currency
    this.status = payoutItemEntity.status
    this.last4Digits = payoutItemEntity.last4Digits
    this.bankName = payoutItemEntity.bankName
    this.createdAt = payoutItemEntity.createdAt
    this.expectedReceptionDate = payoutItemEntity.expectedReceptionDate
    this.intervalStartDate = payoutItemEntity.intervalStartDate
    this.intervalEndDate = payoutItemEntity.intervalEndDate
  }

  static fromItemDto(dto: PayoutItemDto): PayoutItemEntity {
    return new PayoutItemEntity({
      id: dto.id,
      amount: dto.amount,
      currency: dto.currency,
      status: dto.status,
      last4Digits: dto.last4Digits ?? null,
      bankName: dto.bankName ?? null,
      createdAt: new Date(dto.createdAt),
      expectedReceptionDate: new Date(dto.expectedReceptionDate),
      intervalStartDate: dto.intervalStartDate ? new Date(dto.intervalStartDate) : null,
      intervalEndDate: dto.intervalEndDate ? new Date(dto.intervalEndDate) : null,
    })
  }

  getFormatedDigits = (): string => {
    return `····${this.last4Digits}`
  }

  getPayoutStatusWord = (): string => {
    switch (this.status) {
      case PayoutTransferStatus.PAID:
        return word("payoutTransferStatus.paid")
      case PayoutTransferStatus.IN_TRANSIT:
        return word("payoutTransferStatus.inTransit")
      case PayoutTransferStatus.CANCELED:
        return word("payoutTransferStatus.canceled")
      case PayoutTransferStatus.FAILED:
        return word("payoutTransferStatus.failed")
      case PayoutTransferStatus.PENDING:
        return word("payoutTransferStatus.pending")
      default:
        return ""
    }
  }

  getPayoutStatusColor = () => {
    switch (this.status) {
      case PayoutTransferStatus.PAID:
        return "text-textSuccess"
      case PayoutTransferStatus.IN_TRANSIT:
        return "text-textSuccess"
      case PayoutTransferStatus.CANCELED:
        return "text-textDefault-body"
      case PayoutTransferStatus.FAILED:
        return "text-red-500"
      case PayoutTransferStatus.PENDING:
        return "text-custom-orange-500"
      default: {
        const exhaustiveCheck: never = this.status
        throw new Error(`Invalid payout status: ${String(exhaustiveCheck)}`)
      }
    }
  }

  getPayoutStatusDetailsWord = () => {
    switch (this.status) {
      case PayoutTransferStatus.PAID:
        return dayjs(this.expectedReceptionDate).format("L")
      case PayoutTransferStatus.IN_TRANSIT:
      case PayoutTransferStatus.PENDING:
      case PayoutTransferStatus.CANCELED:
      case PayoutTransferStatus.FAILED:
        return null
      default: {
        const exhaustiveCheck: never = this.status
        throw new Error(`Invalid payout status: ${String(exhaustiveCheck)}`)
      }
    }
  }

  getPayoutStatusDetailsColor = () => {
    switch (this.status) {
      case PayoutTransferStatus.PAID:
        return "text-textDefault-body"
      case PayoutTransferStatus.IN_TRANSIT:
      case PayoutTransferStatus.PENDING:
      case PayoutTransferStatus.CANCELED:
      case PayoutTransferStatus.FAILED:
        return null
      default: {
        const exhaustiveCheck: never = this.status
        throw new Error(`Invalid payout status: ${String(exhaustiveCheck)}`)
      }
    }
  }
}

export type PayoutTransactionState = Omit<PayoutTransactionEntity, "getWordBalanceTransactionStatus">
export class PayoutTransactionEntity {
  transactionId: string | null
  status: TransactionStatus
  amount: number
  currency: string
  fee: number
  name?: string
  createdAt: Date

  private constructor(payoutTransactionEntity: PayoutTransactionState) {
    this.transactionId = payoutTransactionEntity.transactionId
    this.status = payoutTransactionEntity.status
    this.amount = payoutTransactionEntity.amount
    this.currency = payoutTransactionEntity.currency
    this.fee = payoutTransactionEntity.fee
    this.name = payoutTransactionEntity.name
    this.createdAt = payoutTransactionEntity.createdAt
  }

  static fromDto(dto: PayoutTransactionDto): PayoutTransactionEntity {
    return new PayoutTransactionEntity({
      transactionId: dto.transactionId,
      status: dto.status,
      amount: dto.amount,
      currency: dto.currency,
      fee: dto.fee,
      name: dto.name ?? undefined,
      createdAt: new Date(dto.createdAt),
    })
  }

  getWordBalanceTransactionStatus = () => {
    switch (this.status) {
      case TransactionStatus.DISPUTED:
        return word("balanceTransactionType.adjustment")
      case TransactionStatus.REFUNDED:
        return word("balanceTransactionType.refund")
      case TransactionStatus.SUCCEEDED:
        return word("balanceTransactionType.payment")
      default:
        return this.status
          .split("_")
          .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
          .join(" ")
    }
  }
}

export type TransactionSummaryState = Omit<TransactionSummaryEntity, "getTotalLessFee">
export class TransactionSummaryEntity {
  number: number
  feeAmount?: number
  totalAmount?: number
  currency?: string

  private constructor(transactionSummaryEntity: TransactionSummaryState) {
    this.number = transactionSummaryEntity.number
    this.feeAmount = transactionSummaryEntity.feeAmount
    this.totalAmount = transactionSummaryEntity.totalAmount
    this.currency = transactionSummaryEntity.currency
  }

  static fromDto(dto: TransactionSummaryDto): TransactionSummaryEntity {
    return new TransactionSummaryEntity({
      number: dto.number,
      feeAmount: dto.feeAmount ?? undefined,
      totalAmount: dto.totalAmount ?? undefined,
      currency: dto.currency ?? undefined,
    })
  }

  getTotalLessFee(): number {
    return (this.totalAmount ?? 0) - (this.feeAmount ?? 0)
  }
}

export type PayoutDetailsState = Omit<PayoutDetailsEntity, "getFormatedDigits" | "getPayoutStatusWord">
export class PayoutDetailsEntity {
  id: string
  amount: number
  currency: string
  status: PayoutTransferStatus
  last4Digits: string | null
  bankName: string | null
  createdAt: Date
  expectedReceptionDate: Date
  intervalStartDate: Date | null
  intervalEndDate: Date | null
  bankCountry: string | null
  routingNumber: string | null
  transferTrackingId: string | null
  note: string | null
  transferMethod: TransferMethod
  transactions: PayoutTransactionEntity[]
  paymentSummary: TransactionSummaryEntity
  refundSummary: TransactionSummaryEntity
  adjustmentSummary: TransactionSummaryEntity

  private constructor(payoutDetailsEntity: PayoutDetailsState) {
    this.id = payoutDetailsEntity.id
    this.amount = payoutDetailsEntity.amount
    this.currency = payoutDetailsEntity.currency
    this.status = payoutDetailsEntity.status
    this.last4Digits = payoutDetailsEntity.last4Digits
    this.bankName = payoutDetailsEntity.bankName
    this.createdAt = payoutDetailsEntity.createdAt
    this.expectedReceptionDate = payoutDetailsEntity.expectedReceptionDate
    this.intervalStartDate = payoutDetailsEntity.intervalStartDate
    this.intervalEndDate = payoutDetailsEntity.intervalEndDate
    this.bankCountry = payoutDetailsEntity.bankCountry
    this.routingNumber = payoutDetailsEntity.routingNumber
    this.transferTrackingId = payoutDetailsEntity.transferTrackingId
    this.note = payoutDetailsEntity.note
    this.transferMethod = payoutDetailsEntity.transferMethod
    this.transactions = payoutDetailsEntity.transactions
    this.paymentSummary = payoutDetailsEntity.paymentSummary
    this.refundSummary = payoutDetailsEntity.refundSummary
    this.adjustmentSummary = payoutDetailsEntity.adjustmentSummary
  }

  static fromDto(dto: PayoutDetailsDto): PayoutDetailsEntity {
    return new PayoutDetailsEntity({
      id: dto.id,
      amount: dto.amount,
      currency: dto.currency,
      status: dto.status,
      last4Digits: dto.last4Digits ?? null,
      bankName: dto.bankName ?? null,
      createdAt: new Date(dto.createdAt),
      expectedReceptionDate: new Date(dto.expectedReceptionDate),
      intervalStartDate: dto.intervalStartDate ? new Date(dto.intervalStartDate) : null,
      intervalEndDate: dto.intervalEndDate ? new Date(dto.intervalEndDate) : null,
      bankCountry: dto.bankCountry,
      routingNumber: dto.routingNumber,
      transferTrackingId: dto.transferTrackingId,
      note: dto.note,
      transferMethod: dto.transferMethod,
      transactions: dto.transactions.map((transaction) => PayoutTransactionEntity.fromDto(transaction)),
      paymentSummary: TransactionSummaryEntity.fromDto(dto.paymentSummary),
      refundSummary: TransactionSummaryEntity.fromDto(dto.refundSummary),
      adjustmentSummary: TransactionSummaryEntity.fromDto(dto.adjustmentSummary),
    })
  }

  getFormatedDigits = (): string => {
    return `····${this.last4Digits}`
  }

  getPayoutStatusWord = (): string => {
    switch (this.status) {
      case PayoutTransferStatus.PAID:
        return word("payoutTransferStatus.paid")
      case PayoutTransferStatus.IN_TRANSIT:
        return word("payoutTransferStatus.inTransit")
      case PayoutTransferStatus.CANCELED:
        return word("payoutTransferStatus.canceled")
      case PayoutTransferStatus.FAILED:
        return word("payoutTransferStatus.failed")
      case PayoutTransferStatus.PENDING:
        return word("payoutTransferStatus.pending")
      default:
        return ""
    }
  }
}
